/**
 * Payment Pending Page
 *
 * Shown to users whose profile was approved and need to complete payment
 */

import { useState } from 'preact/hooks';
import { useAuth } from '../../hooks/useAuth';

export function PaymentPending() {
  const { user } = useAuth();
  const [copied, setCopied] = useState<'bizum' | 'iban' | 'concept' | null>(null);

  const copyToClipboard = (text: string, type: 'bizum' | 'iban' | 'concept') => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  if (!user) {
    return null;
  }

  // Payment details (TODO: Move these to environment variables or backend config)
  const bizumNumber = '+34 612 345 678';
  const ibanNumber = 'ES12 3456 7890 1234 5678 9012';
  const paymentAmount = '120€';
  const paymentConcept = `FIT-${user.id}`;
  const paymentEmail = 'pagos@fitbook.es';

  return (
    <div class="max-w-3xl mx-auto">
          <div class="bg-white shadow-lg rounded-lg overflow-hidden">
            {/* Success header */}
            <div class="bg-green-50 px-6 py-8 text-center">
              <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 class="text-3xl font-bold text-gray-900 mb-2">
                ¡Felicidades! Tu Perfil ha sido Aprobado
              </h2>
              <p class="text-lg text-gray-600">
                Solo falta un último paso para activar tu cuenta
              </p>
            </div>

            {/* Payment instructions */}
            <div class="px-6 py-8">
              <div class="space-y-8">
                {/* Amount */}
                <div class="text-center pb-6 border-b border-gray-200">
                  <p class="text-sm text-gray-500 mb-1">Cuota anual de membresía</p>
                  <p class="text-4xl font-bold text-gray-900">{paymentAmount}</p>
                  <p class="text-sm text-gray-500 mt-1">IVA incluido</p>
                </div>

                {/* Payment options */}
                <div>
                  <h3 class="text-lg font-semibold text-gray-900 mb-4">
                    Opciones de Pago
                  </h3>

                  {/* Bizum option */}
                  <div class="bg-gray-50 rounded-lg p-4 mb-4">
                    <div class="flex items-start justify-between">
                      <div class="flex-1">
                        <h4 class="font-semibold text-gray-900 mb-2 flex items-center">
                          <svg class="w-5 h-5 mr-2 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                          </svg>
                          Opción 1: Bizum
                        </h4>
                        <div class="flex items-center justify-between bg-white rounded p-3 mb-2">
                          <span class="text-lg font-mono text-gray-900">{bizumNumber}</span>
                          <button
                            onClick={() => copyToClipboard(bizumNumber, 'bizum')}
                            class="ml-4 px-3 py-1 text-sm bg-blue-500 hover:bg-blue-600 text-white rounded transition"
                          >
                            {copied === 'bizum' ? '✓ Copiado' : 'Copiar'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bank transfer option */}
                  <div class="bg-gray-50 rounded-lg p-4">
                    <div class="flex items-start justify-between">
                      <div class="flex-1">
                        <h4 class="font-semibold text-gray-900 mb-2 flex items-center">
                          <svg class="w-5 h-5 mr-2 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                            <path fill-rule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clip-rule="evenodd" />
                          </svg>
                          Opción 2: Transferencia Bancaria
                        </h4>
                        <div class="flex items-center justify-between bg-white rounded p-3 mb-2">
                          <span class="text-sm font-mono text-gray-900">{ibanNumber}</span>
                          <button
                            onClick={() => copyToClipboard(ibanNumber, 'iban')}
                            class="ml-4 px-3 py-1 text-sm bg-blue-500 hover:bg-blue-600 text-white rounded transition"
                          >
                            {copied === 'iban' ? '✓ Copiado' : 'Copiar'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment concept */}
                <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                  <div class="flex">
                    <div class="flex-shrink-0">
                      <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                      </svg>
                    </div>
                    <div class="ml-3 flex-1">
                      <h4 class="text-sm font-semibold text-yellow-800 mb-2">
                        IMPORTANTE: Incluir concepto de pago
                      </h4>
                      <div class="flex items-center justify-between bg-white rounded p-3">
                        <div>
                          <p class="text-sm text-gray-600 mb-1">Concepto:</p>
                          <span class="text-lg font-mono font-bold text-gray-900">{paymentConcept}</span>
                        </div>
                        <button
                          onClick={() => copyToClipboard(paymentConcept, 'concept')}
                          class="ml-4 px-3 py-1 text-sm bg-yellow-500 hover:bg-yellow-600 text-white rounded transition"
                        >
                          {copied === 'concept' ? '✓ Copiado' : 'Copiar'}
                        </button>
                      </div>
                      <p class="text-xs text-yellow-700 mt-2">
                        El concepto es necesario para identificar tu pago y activar tu cuenta automáticamente.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Next steps */}
                <div>
                  <h3 class="text-lg font-semibold text-gray-900 mb-3">
                    Próximos Pasos
                  </h3>
                  <div class="space-y-3">
                    <div class="flex">
                      <span class="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-semibold mr-3">
                        1
                      </span>
                      <p class="text-gray-700">
                        Realiza el pago usando Bizum o transferencia bancaria con el concepto <strong>{paymentConcept}</strong>
                      </p>
                    </div>
                    <div class="flex">
                      <span class="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-semibold mr-3">
                        2
                      </span>
                      <p class="text-gray-700">
                        Envía el comprobante de pago a{' '}
                        <a href={`mailto:${paymentEmail}`} class="text-blue-600 hover:text-blue-800 font-medium">
                          {paymentEmail}
                        </a>
                      </p>
                    </div>
                    <div class="flex">
                      <span class="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-semibold mr-3">
                        3
                      </span>
                      <p class="text-gray-700">
                        Una vez verificado el pago, tu cuenta se activará automáticamente (1-2 días hábiles)
                      </p>
                    </div>
                  </div>
                </div>

                {/* Help */}
                <div class="bg-blue-50 border-l-4 border-blue-400 p-4">
                  <div class="flex">
                    <div class="flex-shrink-0">
                      <svg class="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                      </svg>
                    </div>
                    <div class="ml-3">
                      <h4 class="text-sm font-semibold text-blue-800 mb-1">
                        ¿Necesitas ayuda?
                      </h4>
                      <p class="text-sm text-blue-700">
                        Si tienes problemas con el pago o preguntas, contáctanos en{' '}
                        <a href={`mailto:${paymentEmail}`} class="font-medium underline">
                          {paymentEmail}
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* User status */}
          <div class="mt-6 bg-white shadow rounded-lg px-6 py-4">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-500">Usuario</p>
                <p class="text-lg font-medium text-gray-900">{user.email}</p>
              </div>
              <div class="text-right">
                <p class="text-sm text-gray-500">Estado actual</p>
                <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  Aprobado - Pago Pendiente
                </span>
              </div>
            </div>
          </div>
    </div>
  );
}
